import Drawing from "dxf-writer";

// Pure, paper-free DXF serialization. Kept separate from dxfExporter.ts so it
// can be unit-tested in jsdom without pulling in paper.js (which needs a real
// canvas at import). dxfExporter.ts gathers geometry into `DxfEntity[]` and
// hands it to this serializer.

export type LayerName =
  | "CUT"
  | "HOLE"
  | "ENGRAVE"
  | "CONSTRUCTION"
  // Seam polylines (#572, "seams" stitch mode) and the size-calibration
  // squares — kept on their own layers so CAM tools can assign (or drop)
  // each independently of the cut geometry.
  | "STITCH"
  | "CALIBRATION";

/**
 * Format-agnostic geometry in CAD coordinates (y-up, millimetres). Each
 * serializer emits these in its own dialect, scaling to the target unit.
 */
export type DxfEntity =
  | {
      kind: "polyline";
      layer: LayerName;
      points: [number, number][];
      closed: boolean;
    }
  | { kind: "circle"; layer: LayerName; x: number; y: number; r: number };

export const LAYER_DEFS: {
  name: LayerName;
  aci: number;
  lineType: "CONTINUOUS" | "DASHED";
}[] = [
  { name: "CUT", aci: 1 /* red */, lineType: "CONTINUOUS" },
  { name: "HOLE", aci: 5 /* blue */, lineType: "CONTINUOUS" },
  { name: "ENGRAVE", aci: 7 /* white/black */, lineType: "CONTINUOUS" },
  { name: "CONSTRUCTION", aci: 3 /* green */, lineType: "DASHED" },
  { name: "STITCH", aci: 6 /* magenta */, lineType: "CONTINUOUS" },
  { name: "CALIBRATION", aci: 5 /* blue */, lineType: "CONTINUOUS" },
];

// ---------------------------------------------------------------------------
// Modern (AC1021 / LWPOLYLINE) serializer — via dxf-writer.
// ---------------------------------------------------------------------------

export function toModernDxf(entities: DxfEntity[]): string {
  const d = new Drawing();
  d.setUnits("Millimeters");
  for (const l of LAYER_DEFS) d.addLayer(l.name, l.aci, l.lineType);

  for (const e of entities) {
    if (e.kind === "circle") {
      d.setActiveLayer(e.layer).drawCircle(e.x, e.y, e.r);
    } else {
      d.setActiveLayer(e.layer);
      d.drawPolyline(e.points, e.closed);
    }
  }
  return d.toDxfString();
}
